import { NULL_COLOR } from "./pages/media/components/storyboardEditor/constants";

export const fetchText = async (path: string) => {
  const response = await fetch(path);
  return await response.text();
};

export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const hexToRgba = (
  hex?: string,
  alpha = 255
): [number, number, number, number] => {
  if (!hex) return [0, 0, 0, 0];
  hex = hex.replace("#", "");

  // Extract the RGB components (first 6 characters)
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);

  // Extract the alpha component (last 2 characters) and convert to a value between 0 and 255
  if (hex.length > 6) {
    const alpha255 = Number.parseInt(hex.slice(6, 8), 16);
    // if alpha format is 0-1, convert it to 0-1
    if (alpha === 1) {
      alpha = Math.round((alpha255 / 255) * 10) / 10;
    } else {
      alpha = alpha255;
    }
  }

  return [r, g, b, alpha] as [number, number, number, number];
};

export function rgbaToHex(rgba?: [number, number, number, number] | string) {
  if (!rgba) return NULL_COLOR;
  // workaround to simplify types from api returning tuples or string
  if (typeof rgba === "string") return rgba; // seem to be already a hex !

  const [r, g, b, a] = rgba;
  const redHex = r.toString(16).padStart(2, "0");
  const greenHex = g.toString(16).padStart(2, "0");
  const blueHex = b.toString(16).padStart(2, "0");

  // Convert alpha to a fraction (0-1), then multiply by 255 and convert to hex
  const alphaHex = a.toString(16).padStart(2, "0");

  return `#${redHex}${greenHex}${blueHex}${alphaHex}`;
}

// Extract RGB(A)
export const parseColor = (color: string) => {
  let [r, g, b, a] = [0, 0, 0, 0];
  if (color.startsWith("#")) {
    let expandedHex = color;
    if (color.length === 4) {
      // Convert short hex to expanded one #fff => #ffffff
      expandedHex =
        "#" + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];
    }
    const rgba = hexToRgba(expandedHex, 1);
    return { r: rgba[0], g: rgba[1], b: rgba[2], a: rgba[3] };
  } else if (color.startsWith("rgba")) {
    const match = color.match(/[\d.]+/g);
    if (match !== null) {
      [r, g, b, a] = match.map(Number);
    }
    return { r, g, b, a };
  } else if (color.startsWith("rgb")) {
    const match = color.match(/[\d.]+/g);
    if (match !== null) {
      [r, g, b] = match.map(Number);
    }
    return { r, g, b, a: 1 };
  } else {
    throw new Error("Unsupported color format");
  }
};

export function lightenColor(
  color: string,
  lightenFactor = 0.2, // how much to lighten, between 0 and 1
  opacity?: number // optional opacity between 0 and 1
): string {
  const clamp = (val: number, min = 0, max = 255) =>
    Math.min(Math.max(val, min), max);

  const { r, g, b, a } = parseColor(color);

  const lighten = (c: number) => clamp(c + (255 - c) * lightenFactor);

  const newR = lighten(r);
  const newG = lighten(g);
  const newB = lighten(b);
  const newA = opacity ?? a;

  return `rgba(${Math.round(newR)}, ${Math.round(newG)}, ${Math.round(newB)}, ${newA})`;
}

export const removeOpacityFromHex = (hex: string) => {
  return hex?.length === 9 ? hex?.slice(0, 7) : hex;
};
